import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { mergeDeep, mergeSiteCopy, SITE_COPY_CONTENT_KEY } from "@tih/shared";
import { ApiError, adminFetchContent, adminPutContent } from "../../api/client";
import { AdminCopySection } from "../../components/admin/AdminCopyField";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { ADMIN_COPY_TAB_IDS, ADMIN_COPY_TABS } from "../../lib/adminCopySections";

function isCopyTab(raw: string | null): raw is (typeof ADMIN_COPY_TAB_IDS)[number] {
  return raw != null && (ADMIN_COPY_TAB_IDS as readonly string[]).includes(raw);
}

function patchesEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function AdminCopyPage() {
  const { refresh: refreshPublicCopy } = useSiteCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = isCopyTab(tabParam) ? tabParam : "home";
  const activeTabDef = ADMIN_COPY_TABS.find((t) => t.id === activeTab) ?? ADMIN_COPY_TABS[0]!;

  const [patch, setPatch] = useState<Record<string, unknown>>({});
  const savedPatchRef = useRef<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const dirty = useMemo(
    () => !patchesEqual(patch, savedPatchRef.current),
    [patch],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminFetchContent(SITE_COPY_CONTENT_KEY);
      const next = structuredClone(r.valueJson as Record<string, unknown>);
      savedPatchRef.current = structuredClone(next);
      setPatch(next);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        savedPatchRef.current = {};
        setPatch({});
      } else {
        setError(String((e as Error).message ?? e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const merged = useMemo(() => mergeSiteCopy(patch), [patch]);

  function selectTab(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", id);
      return next;
    });
  }

  function onDiscard() {
    setPatch(structuredClone(savedPatchRef.current));
    setSuccess(null);
    setError(null);
  }

  async function onSave(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      let latest: Record<string, unknown> = {};
      try {
        const r = await adminFetchContent(SITE_COPY_CONTENT_KEY);
        latest = structuredClone(r.valueJson as Record<string, unknown>);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
      }
      const toSave = mergeDeep(latest, patch);
      await adminPutContent(SITE_COPY_CONTENT_KEY, toSave);
      savedPatchRef.current = structuredClone(toSave);
      setPatch(structuredClone(toSave));
      await refreshPublicCopy();
      setSuccess("Saved — visitors will see your changes right away.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="admin-page admin-copy-page" style={{ opacity: 0.5 }}>
        Loading…
      </div>
    );
  }

  return (
    <div className={`admin-page admin-copy-page${dirty ? " admin-copy-page--dirty" : ""}`}>
      <header className="admin-page-header">
        <h1>Marketing copy</h1>
        <p>
          Edit the words visitors read across the site, grouped by page. You can also click the{" "}
          <span className="admin-copy-inline-pencil">✎</span> pencil on the live site while signed in.
          Power users can edit the{" "}
          <Link to={`/admin/content/json?key=${SITE_COPY_CONTENT_KEY}`}>raw JSON</Link>.
        </p>
      </header>

      <nav className="admin-tabs admin-copy-tabs" aria-label="Copy sections">
        {ADMIN_COPY_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={tab.id === activeTab ? "active" : undefined}
            onClick={() => selectTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success admin-copy-flash" role="status">
          {success}
        </div>
      )}

      <form className="admin-copy-form" onSubmit={(e) => void onSave(e)}>
        <div className="admin-copy-panels">
          {activeTabDef.sections.map((section) => (
            <AdminCopySection
              key={section.id}
              section={section}
              merged={merged}
              patch={patch}
              setPatch={setPatch}
            />
          ))}
        </div>

        <div className={`admin-copy-save-bar${dirty ? " admin-copy-save-bar--visible" : ""}`}>
          <div className="admin-copy-save-bar-inner">
            <span className="admin-copy-save-bar-note">
              {dirty ? "You have unsaved changes" : "All changes saved"}
            </span>
            <div className="admin-copy-save-bar-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy || !dirty}
                onClick={onDiscard}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => void load()}
              >
                Reload
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || !dirty}>
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
